import {Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {MvsCrudService} from "../../../../core/shared/service/crud/mvs-crud.service";
import {CbConfigurationEntityDto} from "../../model/dto/api/cb-configuration-entity.dto";
import {CbRecordDocGenerateRequestDto} from "../../model/dto/api/cb-record-doc-generate-request.dto";
import {CbRecordDocGenerateResultDto} from "../../model/dto/api/cb-record-doc-generate-result.dto";

/**
 * Service for record documentation generation API endpoints.
 */
@Injectable({
    providedIn: 'root'
})
export class CbRecordDocumentationApiService {

    private readonly baseUrl = MvsCrudService.baseUrl + '/cb/record-documentation';

    constructor(protected http: HttpClient) {
    }

    /**
     * Get all modules that have configuration entities.
     */
    getModules(): Observable<string[]> {
        return this.http.get<string[]>(`${this.baseUrl}/modules`);
    }

    /**
     * Get all configuration entities, optionally filtered by module.
     */
    getConfigurationEntities(moduleName?: string): Observable<CbConfigurationEntityDto[]> {
        const params = moduleName ? {moduleName} : {};
        return this.http.get<CbConfigurationEntityDto[]>(`${this.baseUrl}/configuration-entities`, {params});
    }

    /**
     * Get configuration entities for a specific module.
     */
    getConfigurationEntitiesByModule(moduleName: string): Observable<CbConfigurationEntityDto[]> {
        return this.http.get<CbConfigurationEntityDto[]>(`${this.baseUrl}/configuration-entities/${moduleName}`);
    }

    /**
     * Generate AI-powered documentation for entity records.
     */
    generateDocumentation(request: CbRecordDocGenerateRequestDto): Observable<CbRecordDocGenerateResultDto> {
        return this.http.post<CbRecordDocGenerateResultDto>(`${this.baseUrl}/generate`, request);
    }

    /**
     * Generate documentation for a single entity type.
     */
    generateForEntity(
        objectTypeAlias: string,
        overwriteExisting: boolean = false,
        additionalContext?: string
    ): Observable<CbRecordDocGenerateResultDto> {
        const params: any = {overwriteExisting: overwriteExisting.toString()};
        if (additionalContext) {
            params.additionalContext = additionalContext;
        }
        return this.http.post<CbRecordDocGenerateResultDto>(
            `${this.baseUrl}/generate/${objectTypeAlias}`,
            null,
            {params}
        );
    }

    /**
     * Get summary statistics for all configuration entities.
     */
    getSummary(): Observable<{
        totalEntities: number;
        totalRecords: number;
        documentedRecords: number;
        overallCoveragePercentage: number;
        fullyDocumentedEntities: number;
        entitiesWithNoDocumentation: number;
    }> {
        return this.http.get<any>(`${this.baseUrl}/summary`);
    }
}
